import { GraphRelation, GraphNode, GraphEdge } from '../../interfaces/visualization.interface';
import { ElementDefinition } from 'cytoscape';

const NODE_SEPARATOR = '___VOYAGER_NODE___';

export function mapRelationsToCytoscapeElements(relations: GraphRelation[]): ElementDefinition[] {
  const nodeMap = new Map<string, GraphNode>();
  const edgeMap = new Map<string, GraphEdge>();

  // Track which nodes appear as source or target
  const sourceCounts = new Map<string, number>();
  const targetCounts = new Map<string, number>();

  // First pass: collect all nodes and count appearances
  for (const relation of relations) {
    if (relation.source) {
      sourceCounts.set(relation.source, (sourceCounts.get(relation.source) || 0) + 1);
      if (!nodeMap.has(relation.source)) {
        nodeMap.set(relation.source, {
          id: relation.source,
          label: relation.source,
          isSource: true,
          isTarget: false
        });
      }
    }

    if (relation.target) {
      targetCounts.set(relation.target, (targetCounts.get(relation.target) || 0) + 1);
      if (!nodeMap.has(relation.target)) {
        nodeMap.set(relation.target, {
          id: relation.target,
          label: relation.target,
          isSource: false,
          isTarget: true
        });
      }
    }
  }

  // Update isSource/isTarget flags based on actual counts
  for (const [nodeId, node] of nodeMap) {
    node.isSource = (sourceCounts.get(nodeId) || 0) > 0;
    node.isTarget = (targetCounts.get(nodeId) || 0) > 0;
  }

  // Second pass: create edges
  for (let i = 0; i < relations.length; i++) {
    const relation = relations[i];
    if (!relation.source || !relation.target) continue;

    const edgeId = `${relation.source}→${relation.target}__${i}`;
    edgeMap.set(edgeId, {
      id: edgeId,
      source: relation.source,
      target: relation.target,
      label: relation.relationship || ''
    });
  }

  // Build cytoscape elements
  const elements: ElementDefinition[] = [];

  // Add nodes
  for (const node of nodeMap.values()) {
    const isSourceOnly = node.isSource && !node.isTarget;
    const shape = getNodeShape(node.label);

    elements.push({
      group: 'nodes',
      data: {
        id: node.id,
        label: node.label,
        shape: shape,
        isSourceOnly: isSourceOnly,
        nodeType: isSourceOnly ? 'source' : (node.isTarget && !node.isSource ? 'target' : 'intermediate')
      }
    });
  }

  // Add edges
  for (const edge of edgeMap.values()) {
    elements.push({
      group: 'edges',
      data: {
        id: edge.id,
        source: edge.source,
        target: edge.target,
        label: edge.label
      }
    });
  }

  return elements;
}

function getNodeShape(label: string): string {
  const length = label.length;
  if (length <= 8) return 'ellipse';
  if (length <= 15) return 'round-rectangle';
  return 'rectangle';
}